import { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DropDown } from "@normasteaching/norma-ui";
import { Ellipsis } from "lucide-react";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import { useMessageController } from "../../UI/useMessageController.ts";
import { PromoDTO } from "../../ProductService/NormasTeachingPromo/useAllPromos.ts";
import { useSetPromoStatus } from "../../ProductService/NormasTeachingPromo/useSetPromoStatus.ts";
import { useNavigate } from "react-router";
import { mutate } from "swr";
import { PromoDetailModal } from "./PromoDetailModal.tsx";
import { ActiveBadge } from "../../UI/Admin/ActiveBadge.tsx";

function getPromoStatus(promo: PromoDTO): boolean {
  const now = new Date();
  if (!promo.isActive) return false;
  if (promo.validFrom && now < new Date(promo.validFrom)) return false;
  if (promo.validTo && now > new Date(promo.validTo)) return false;
  return true;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const columnHelper = createColumnHelper<PromoDTO>();

const columns = [
  columnHelper.accessor("name", {
    header: "Nome",
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor((row) => row.bonuses.length, {
    id: "bonusCount",
    header: "Bonus",
    cell: (info) => `${info.getValue()} bonus`,
    maxSize: 100,
  }),
  columnHelper.accessor("validFrom", {
    header: "Valid From",
    cell: (info) => formatDate(info.getValue()),
    maxSize: 120,
  }),
  columnHelper.accessor("validTo", {
    header: "Valid To",
    cell: (info) => formatDate(info.getValue()),
    maxSize: 120,
  }),
  columnHelper.accessor("isActive", {
    header: "Status",
    cell: (info) => <ActiveBadge active={getPromoStatus(info.row.original)} />,
    maxSize: 100,
  }),
  columnHelper.display({
    id: "actions",
    cell: function Actions(props) {
      const { showMessage, open, message } = useMessageController();
      const navigate = useNavigate();
      const { setPromoStatus } = useSetPromoStatus();
      const promo = props.row.original;

      return (
        <>
          <SuccessMessage open={open} message={message} />
          <DropDown activator={<Ellipsis className="w-5" />}>
            <div>
              <div
                className="p-3 hover:bg-primary hover:text-white cursor-pointer"
                onClick={() =>
                  navigate(`/admin/normasteaching/edit-promo/${promo._id}`)
                }
              >
                Modifica
              </div>
              <div
                className="p-3 hover:bg-primary hover:text-white cursor-pointer"
                onClick={async () => {
                  await setPromoStatus(promo._id, !promo.isActive);
                  await mutate("/promos");
                  showMessage({
                    message: `Promo "${promo.name}" ${promo.isActive ? "disattivata" : "attivata"} con successo!`,
                    timeoutMs: 4000,
                  });
                }}
              >
                {promo.isActive ? "Disattiva" : "Attiva"}
              </div>
            </div>
          </DropDown>
        </>
      );
    },
  }),
];

export function AdminPromoTable({ promos }: { promos: PromoDTO[] }) {
  const [selectedPromo, setSelectedPromo] = useState<PromoDTO | null>(null);

  const table = useReactTable({
    data: promos,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {selectedPromo && (
        <PromoDetailModal
          promo={selectedPromo}
          onClose={() => setSelectedPromo(null)}
        />
      )}
      <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th className="px-6 py-4" key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
              key={row.id}
              onClick={() => setSelectedPromo(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  style={{
                    maxWidth: cell.column.columnDef.maxSize ?? "none",
                    overflow:
                      cell.column.id !== "actions" ? "hidden" : undefined,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  className="px-6 py-4"
                  key={cell.id}
                  onClick={
                    cell.column.id === "actions"
                      ? (e) => e.stopPropagation()
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
